import { Skeleton, SkeletonTableCell, TableProps, getTableMetaHelper } from '@round/ui-kit';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useYoutubeReportCampaignPosts, YoutubeReportPostsTableRow } from './useYoutubeReportCampaignPosts';
import { CampaignTableRow } from '../../Campaigns/CampaignsTable/ReportCampaignsTable';
import CostCell from 'Modules/Plans/components/CostCell/CostCell';
import TotalCostFooterCell from 'Modules/Plans/components/TotalCostFooterCell/TotalCostFooterCell';
import NotesCell from 'Modules/Plans/components/NotesCell/NotesCell';
import VirtualizedPostsTable from 'Modules/Plans/Posts/components/PostsTable/VirtualizedPostsTable';
import postsTableStyles from 'Modules/Plans/Posts/components/PostsTable/PostsTable.module.css';
import PostsTableStatusCell, {
    StatusData,
} from 'Modules/Plans/Posts/components/PostsTable/cells/StatusCell/PostsTableStatusCell';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import PostStatsValueCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsValueCell';
import PostStatsViewsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostStatsViewsFooterCell';
import PostStatsFooterTotalCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableStatFooterCell';
import PostsTableAccountCell, {
    AccountData,
} from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableAccountCell/PostsTableAccountCell';
import PostsTableUploadedDateCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableUploadedDateCell';
import { getYoutubeEngagementRatePercentageString, getYoutubeTotalEngagement } from 'Modules/Plans/Posts/helpers';
import PostsTableEngagementRateFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementRateFooterCell';
import PostsTableEngagementsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementsFooterCell';
import { buildYoutubeChannelUrl } from 'helpers';
import { TooltipHeader } from '@round/whitelabel-ui';
import { tooltipCopy } from 'Modules/Plans/helpers';

type Props = Pick<TableProps<YoutubeReportPostsTableRow>, 'noDataLabel'> & {
    campaign: CampaignTableRow;
};

type TableCellContext<K extends keyof YoutubeReportPostsTableRow> = CellContext<
    YoutubeReportPostsTableRow,
    YoutubeReportPostsTableRow[K]
>;

type Meta = BasePostTableMeta<YoutubeReportPostsTableRow> & {
    campaign: CampaignTableRow;
};
const getTableMeta = getTableMetaHelper<Meta>();

const YoutubeReportPostsTable = ({ campaign }: Props) => {
    const {
        data,
        isLoading,
        hasError,
        getIsAccountDataLoading,
        postsTotals,
        areTotalsLoading,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = useYoutubeReportCampaignPosts(campaign.id);

    const columns = useMemo<ColumnDef<YoutubeReportPostsTableRow, any>[]>(
        () => [
            {
                header: 'ID',
                meta: { className: postsTableStyles.idColumn },
                accessorKey: 'id',
                cell: ({ getValue, table }: TableCellContext<'id'>) => {
                    const { isLoading } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return getValue().slice(-8);
                },
            },
            {
                header: 'Account',
                meta: { className: postsTableStyles.reportAccountColumn },
                accessorFn: (row): AccountData => ({
                    image: row.channel?.thumbnail || undefined,
                    title: row.channel?.title || '-',
                    profileUrl: row.channel?.youtube_id ? buildYoutubeChannelUrl(row.channel.youtube_id) : null,
                }),
                id: 'account',
                cell: PostsTableAccountCell,
            },
            {
                header: 'Status',
                meta: { className: postsTableStyles.statusColumn },
                id: 'status',
                accessorFn: (original): StatusData => ({
                    postUrl: original.post_url,
                    platform: original.platform,
                    isReadOnly: true,
                }),
                cell: PostsTableStatusCell,
            },
            {
                header: 'Cost',
                meta: { className: postsTableStyles.costColumn },
                accessorKey: 'cost',
                cell: ({ getValue, table }: TableCellContext<'cost'>) => {
                    const { isLoading, campaign } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }
                    return <CostCell isReadonly cost={getValue()} currency={campaign?.currency ?? null} />;
                },
                footer: ({ table }) => {
                    const { campaign, postsTotals, areTotalsLoading } = getTableMeta(table);

                    if (areTotalsLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <TotalCostFooterCell
                            costs={table.getRowModel().rows.map((r) => r.original.cost)}
                            currency={campaign.currency}
                            total={postsTotals?.cost ?? null}
                        />
                    );
                },
            },
            {
                header: 'Uploaded date',
                meta: { className: postsTableStyles.uploadedDateColumn },
                id: 'uploadedDate',
                cell: PostsTableUploadedDateCell,
            },
            {
                header: 'Views',
                meta: { className: postsTableStyles.statColumn },
                id: 'views',
                accessorFn: (row) => row.view_count,
                cell: PostStatsValueCell,
                footer: PostStatsViewsFooterCell,
            },
            {
                header: 'Total engagements',
                meta: { className: postsTableStyles.totalEngagementsColumn },
                id: 'totalEngagements',
                accessorFn: (row) => getYoutubeTotalEngagement(row),
                cell: PostStatsValueCell,
                footer: PostsTableEngagementsFooterCell,
            },
            {
                header: () => <TooltipHeader header="ER" tooltipBody={tooltipCopy.engagementRate} />,
                meta: { className: postsTableStyles.statColumn },
                id: 'engagementRate',
                accessorFn: (row) => getYoutubeEngagementRatePercentageString(row),
                cell: SkeletonTableCell,
                footer: PostsTableEngagementRateFooterCell,
            },
            {
                header: 'Likes',
                meta: { className: postsTableStyles.statColumn },
                id: 'likes',
                accessorFn: (row) => row.youtube_details?.like_count,
                cell: PostStatsValueCell,
                footer: (context) => <PostStatsFooterTotalCell {...context} totalsField="like_count" />,
            },
            {
                header: 'Comments',
                meta: { className: postsTableStyles.statColumn },
                id: 'comments',
                accessorFn: (row) => row.youtube_details?.comment_count,
                cell: PostStatsValueCell,
                footer: (context) => <PostStatsFooterTotalCell {...context} totalsField="comment_count" />,
            },
            {
                header: 'Notes',
                meta: { className: postsTableStyles.notesColumn },
                accessorKey: 'notes',
                cell: ({ getValue, table }: TableCellContext<'notes'>) => {
                    const { isLoading } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }
                    return <NotesCell notes={getValue()} />;
                },
            },
        ],
        []
    );

    const meta: Meta = {
        isLoading,
        getIsAccountDataLoading,
        campaign,
        managedBy: campaign.managed_by,
        areApiTotalsEnabled: true,
        postsTotals,
        areTotalsLoading,
    };

    return (
        <VirtualizedPostsTable
            columns={columns}
            data={data}
            meta={meta}
            isLoading={isLoading}
            hasError={hasError}
            onEndReached={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
        />
    );
};

export default YoutubeReportPostsTable;
